import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CoreService } from "../Services/Apis/CoreApis";
import {
  ChangeLayout,
  GETA_LAYOUT,
  useAppSelector,
  useLayoutStore,
} from "../Store";
import {
  getLoginDataFromToken,
  logoutAuth,
  setConfigData,
  useAuthStore,
} from "../Store/Slices/AuthSlice";
import { useNotification } from "./useNotification/useNotification";
import {
  setIsUserAvailable,
  useCurrentUser,
} from "../Store/Slices/CurrentUser/CurrentUserSlice";
import LZString from "lz-string";
import { setWithExpiry, validateToken } from "../Utils";
import { getDeviceInfo } from "../Utils/device-details";
import { SubAccountActions } from "../Store/Slices/SubAccount/subaccount.actions";

const redirectToLoginWithNext = (navigate: any, next?: any) => {
  if (!next) {
    next = window.location.href;
  }
  navigate(`/login?next=${next}`);
};

const useAuthentication = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let location = useLocation();
  let { showAlert } = useNotification();
  const { data: authData, userConfig } = useAppSelector(useAuthStore);
  const isWhatsappConfigured = userConfig?.payload?.whatsapp_enabled ?? false;
  const { isUserAvailable } = useAppSelector(useCurrentUser);
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const adminTokenRef = useRef<string | null>(null);
  const [adminTokenState, setAdminTokenState] = useState<string | null>(null);
  const expiryTime = 86400000;
  const handleLogout = () => {
    let data = {
      user_id: authData?.data?.user?.id,
      workspace_id: authData?.data?.user?.workspace_id,
      device:getDeviceInfo(),
    };

    CoreService.userLogout(data).finally(() => {
      dispatch(logoutAuth(null));
      navigate("/login", { replace: true });
      return;
    });
  };

  const reloadLoginFn = () => {
    showAlert(
      "Session verification failed. Please try logging in again.",
      "error"
    );
    const appVersion = window.localStorage.getItem("app_version");
    window.localStorage.clear();
    if (appVersion) {
      window.localStorage.setItem("app_version", appVersion);
    }
    dispatch(logoutAuth(null));
    handleLogout();
    setTimeout(() => {
      redirectToLoginWithNext(navigate, next);
    });
  };
  useEffect(() => {
    if (adminTokenRef.current === null) {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("UTok");
      if (token) {
        const decompressedToken = LZString.decompressFromEncodedURIComponent(token);
        adminTokenRef.current = decompressedToken;
        setAdminTokenState(decompressedToken);
      }
    }
  }, [window.location.search]);
  useEffect(() => {
    if (window.location.hostname !== process.env.REACT_APP_DOMAIN_NAME) {
      dispatch(
        SubAccountActions.getDomainDetails({ domain:window.location.hostname}) as any
      );
    }
  }, [dispatch]);
  // For admin token login
  useLayoutEffect(() => {
    if (adminTokenState) {
      const decodedToken = getLoginDataFromToken({ token: adminTokenState });
      CoreService.tokenVerify()
        .then((res: any) => {
          if (res?.data?.status) {
            let data = res?.data;
            setWithExpiry("expiry_time", data?.expiry_time || expiryTime);
            validateToken(data?.expiry_time || expiryTime);
            dispatch(setConfigData({ payload: decodedToken }));
            // Remove userToken from URL to prevent reload loop
            const url = new URL(window.location.href);
            if (url.searchParams.has('UTok')) {
              url.searchParams.delete('UTok');
              window.history.replaceState({}, document.title, url.pathname + url.search);
            }
            window.location.reload()
          } else {
            setTimeout(() => {
              handleLogout();
              dispatch(logoutAuth(null));
              redirectToLoginWithNext(navigate, next);
            });
          }
        })
        .catch(() => {
          reloadLoginFn();
        });
    }
  }, [adminTokenState]);
  useLayoutEffect(() => {
    if (authData && authData?.data) {
      CoreService.tokenVerify()
        .then((res) => {
          if (res.data) {
            if (!res.data.status) {
              setTimeout(() => {
                handleLogout();
                dispatch(logoutAuth(null));
                redirectToLoginWithNext(navigate, next);
              });
            } else {
              dispatch(setConfigData({ payload: res.data.token }));
              dispatch(setIsUserAvailable(res.data.token?.online_status));
            }
          }
        })
        .catch((err: any) => {
          reloadLoginFn();
        });
    }
  }, [authData?.data]); // eslint-disable-line react-hooks/exhaustive-deps


  const setUserOnlineStatus = (status: boolean) => {
    if (status === isUserAvailable) return;
    dispatch(setIsUserAvailable(status));
  };

  useLayoutEffect(() => {
    if (authData && authData?.data) {
      // Remove automatic redirection to /verify-otp
      // Users should only go to /verify-otp after successful signup
      // setUserOnlineStatus(authData?.data?.user?.onlinestatus);
    }
  }, [authData, location.pathname, isWhatsappConfigured]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(
    () => ({
      isUserLoggedIn: authData?.data?.user?.is_email_verified,
    }),
    [authData?.data?.user]
  );
};

export default useAuthentication;
